/**
 * Dulora cart drawer — vertical "You may also like" recommendations.
 *
 * The Enterprise carousel-slider is horizontal-only, so the cart recs are
 * rendered as a vertical stacked list (snippets/cart-drawer.liquid, grid layout)
 * capped to ~2 rows. This component adds prev/next arrows that page that list
 * up/down. Progressive enhancement: without JS the list keeps a thin scrollbar;
 * with JS the scrollbar is hidden and the arrows take over.
 *
 * The recommendations load asynchronously (product-recommendations fetches its
 * own markup), and the whole cart drawer re-renders on cart changes, so we use a
 * MutationObserver to (re)bind to the <ul> whenever it appears or changes.
 */
if (!customElements.get('dl-cart-recs')) {
  class DlCartRecs extends HTMLElement {
    connectedCallback() {
      this.classList.add('dl-cart-recs--ready');
      this.nav = this.querySelector('.dl-cart-recs__nav');
      this.prevBtn = this.querySelector('[data-dir="prev"]');
      this.nextBtn = this.querySelector('[data-dir="next"]');

      this.onScroll = () => this.update();
      this.prevBtn?.addEventListener('click', () => this.step(-1));
      this.nextBtn?.addEventListener('click', () => this.step(1));

      // Recs load async + the drawer re-renders — rebind whenever the list changes.
      this.obs = new MutationObserver(() => this.bindList());
      this.obs.observe(this, { childList: true, subtree: true });
      this.bindList();
    }

    disconnectedCallback() {
      this.obs?.disconnect();
      this.list?.removeEventListener('scroll', this.onScroll);
    }

    get list() {
      return this.querySelector('product-recommendations ul');
    }

    bindList() {
      const ul = this.list;
      if (ul && ul !== this.boundList) {
        this.boundList?.removeEventListener('scroll', this.onScroll);
        this.boundList = ul;
        ul.addEventListener('scroll', this.onScroll, { passive: true });
      }
      this.update();
    }

    /** Page the list by ~2 rows in the given direction (-1 up, 1 down). */
    step(dir) {
      const ul = this.boundList;
      if (!ul) return;
      const item = ul.querySelector('li');
      const amount = item ? item.offsetHeight * 2 : ul.clientHeight;
      ul.scrollBy({ top: dir * amount, behavior: 'smooth' });
    }

    /** Show the arrows only when there's overflow; disable at the ends. */
    update() {
      const ul = this.boundList;
      const hasOverflow = !!ul && ul.scrollHeight - ul.clientHeight > 1;
      if (this.nav) this.nav.hidden = !hasOverflow;
      if (!ul) return;
      const atTop = ul.scrollTop <= 1;
      const atBottom = ul.scrollTop + ul.clientHeight >= ul.scrollHeight - 1;
      if (this.prevBtn) this.prevBtn.disabled = atTop;
      if (this.nextBtn) this.nextBtn.disabled = atBottom;
    }
  }

  customElements.define('dl-cart-recs', DlCartRecs);
}

/**
 * Dulora cart drawer — Essential Upsell ("Essenail") vertical pager.
 *
 * The Essential Upsell app renders a horizontal carousel; in the Dulora cart
 * drawer it's restyled (dulora-cart.css §11) to a vertical one-product-per-row
 * list capped to ~2 rows. The app's own prev/next arrows only scroll horizontal
 * overflow (now zero), so we rewire them here to page the list vertically, with
 * wrap-around to match the app's looping feel. A thin scrollbar is the fallback.
 *
 * Delegated + capture so it survives the app's re-injections and cart-drawer
 * re-renders, and runs alongside the app's own (now no-op) handler. Scoped to
 * cart-drawer#cart-drawer so the product-page Essential block is untouched.
 */
/**
 * Dulora cart drawer — block stray form submits inside the cart-drawer form.
 *
 * The Essential Upsell ("Essenail") cross-sell block is injected as a child of
 * <cart-items>, which lives inside <form id="cart-drawer-form" method="post">.
 * The app's add-to-cart control is a <button> WITHOUT type="button", so it
 * defaults to type="submit": clicking it submits the drawer form (POST to the
 * cart URL → full-page navigation), which aborts the app's in-flight
 * fetch('/cart/add.js') and surfaces as the app's own
 * "network error when attempting to fetch the results" — the product is never
 * added. (The theme never AJAX-intercepts this form; the only legitimate submit
 * is the checkout button, name="checkout".)
 *
 * Capture-phase + delegated so it runs before the browser's default submit and
 * survives the app's re-injections / drawer re-renders. We allow only the
 * genuine checkout submit through; any other submitter (the app's add button,
 * or any future app-injected implicit-submit control) is neutralised, letting
 * the app's own click handler complete its fetch. Scoped to
 * cart-drawer#cart-drawer so cart-page / other forms are untouched.
 */
if (!window.__dlCartFormSubmitGuard) {
  window.__dlCartFormSubmitGuard = true;
  document.addEventListener(
    'submit',
    function (e) {
      const form = e.target;
      if (!form || form.id !== 'cart-drawer-form') return;
      if (!form.closest('cart-drawer#cart-drawer')) return;

      // The real checkout button is the only intended submit of this form.
      // Only block a positively-identified non-checkout submitter; never block
      // when the submitter is unknown (legacy browsers without event.submitter)
      // so checkout can never be broken by this guard.
      if (!e.submitter || e.submitter.name === 'checkout') return;

      e.preventDefault();
    },
    true
  );
}

if (!window.__dlEssentialPager) {
  window.__dlEssentialPager = true;
  document.addEventListener(
    'click',
    function (e) {
      const btn = e.target.closest(
        '[data-essential-upsell-element="button-left"],[data-essential-upsell-element="button-right"]'
      );
      if (!btn || !btn.closest('cart-drawer#cart-drawer')) return;

      const container = btn.closest(
        '[data-essential-upsell-element="container"][data-essential-upsell-funnel-id]'
      );
      const products =
        container && container.querySelector('[data-essential-upsell-element="products"]');
      if (!products) return;

      const isPrev = btn.matches('[data-essential-upsell-element="button-left"]');
      const row = products.querySelector('[role="group"]');
      const page = row ? row.offsetHeight * 2 + 7 : products.clientHeight; // ~2 rows + gap
      const maxTop = products.scrollHeight - products.clientHeight;
      const atTop = products.scrollTop <= 1;
      const atBottom = products.scrollTop >= maxTop - 1;

      let top;
      if (isPrev) top = atTop ? maxTop : Math.max(0, products.scrollTop - page);
      else top = atBottom ? 0 : Math.min(maxTop, products.scrollTop + page);

      products.scrollTo({ top: top, behavior: 'smooth' });
    },
    true
  );
}
